import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {catchError, Observable, throwError} from 'rxjs';
import {ResponsePacket} from '../../model/ResponsePacket';
import {WorkshopSiteVisitModel} from '../../model/workshop/workshopSiteVisitModel';
import {ScheduleWorkshop} from '../../model/workshop.model';
import {SiteVisitRequestModel} from '../../model/sitevisit.request.model';

@Injectable({
  providedIn: 'root'
})
export class SiteVisitService {
  private FETCH_SITE_VISIT = '/sitevisitscheduling/?_id=#';
  private FETCH_SITE_VISIT_BY_PROJECT_ID = '/sitevisitscheduling/?project=#';
  private SCHEDULE_SITE_VISIT_URL = '/sitevisitscheduling';
  private REJECT_SITE_VISIT_REQUEST = '/sitevisitscheduling/#'
  private UPDATE_SITE_VISIT_REQUEST = '/sitevisitscheduling/#'
  private CONFIRM_SITE_VISIT_REQUEST = '/sitevisitscheduling/confirm/#'
  private PROPOSE_SITE_VISIT_REQUEST = '/sitevisitscheduling/propose/#'
  private RE_SCHEDULE_SITE_VISIT_URL =  '/sitevisitscheduling/#';
  private Site_Visit_Report_Scheduling = '/sitevisitscheduling/';
  private Site_Visit_Report = '/sitevisitreport/#';
  private SITE_VISIT_REPORT_DOWNLOAD =
    '/sitevisitreport/report/download/:project_id?filename=test_file';
  private getUploadDetails = '/sitevisitreport/report/';
  private DELETE_UPLOAD =
    '/sitevisitreport/report/:project_code/:site_visit_report/:filename';
  private SITE_VISIT_UPLOAD = '/sitevisitreport/compliance_reports';
  private GET_SITE_VISIT_UPLOAD =
    '/sitevisitreport/compliance_reports/:site_visit_report';
  public type='Site Visit'

  constructor(private http: HttpClient) { }

  deleteUpload = (
    projectCode: string,
    siteVisitReportId: string,
    filename: string
  ): Observable<any> => {
    const url = this.DELETE_UPLOAD.replace(
      ':project_code',
      encodeURIComponent(projectCode)
    )
      .replace(':site_visit_report', encodeURIComponent(siteVisitReportId))
      .replace(':filename', encodeURIComponent(filename));
    return this.http.delete(url).pipe(catchError(this.handleError));
  };

  getUploadDetail(siteReportId: any, scheduleId?: any) {
    const reportId = String(siteReportId ?? '').trim();
    const schedulingId = String(scheduleId ?? '').trim();

    // SVAGRIHA API requires `_id` (report) or `site_visit_scheduling` (schedule)
    if (reportId && reportId !== 'undefined') {
      const url = `${this.getUploadDetails}?_id=${encodeURIComponent(reportId)}`;
      return this.http.get(url).pipe(catchError(this.handleError));
    }
    if (schedulingId && schedulingId !== 'undefined') {
      const url = `${this.getUploadDetails}?site_visit_scheduling=${encodeURIComponent(schedulingId)}`;
      return this.http.get(url).pipe(catchError(this.handleError));
    }

    return throwError(
      () => new Error('_id or site_visit_scheduling is mandatory'),
    );
  }

  uploadSiteVisit = (body: any): Observable<any> => {
    return this.http
      .post(this.SITE_VISIT_UPLOAD, body)
      .pipe(catchError(this.handleError));
  };

  getSiteVisitUploadDetail(siteReportId: any, _projectCode?: string) {
    const url = this.GET_SITE_VISIT_UPLOAD.replace(
      ':site_visit_report',
      encodeURIComponent(siteReportId)
    );
    return this.http.get(url).pipe(catchError(this.handleError));
  }

  public getSiteVisitReportSchedule(projectId: string) {
    return this.http.get(
      this.Site_Visit_Report_Scheduling + `?project=${projectId}`
    );
  }

  downloadSiteVisitReport(
    projectId: string,
    siteVisitReportId: any,
    filename: string = 'test_file'
  ): Observable<Blob> {
    const url = this.SITE_VISIT_REPORT_DOWNLOAD.replace(
      ':project_id',
      encodeURIComponent(projectId)
    ).replace('test_file', encodeURIComponent(filename));

    const separator = url.includes('?') ? '&' : '?';
    const urlWithId = `${url}${separator}site_visit_report=${encodeURIComponent(
      siteVisitReportId
    )}`;

    return this.http.get(urlWithId, {
      responseType: 'blob',
    }) as Observable<Blob>;
  }

  fetchById = (id: string) : Observable<ResponsePacket<WorkshopSiteVisitModel[]>> => {
    return this.http.get<ResponsePacket<WorkshopSiteVisitModel[]>>(this.FETCH_SITE_VISIT.replace('#', id))
      .pipe(
        catchError(this.handleError)
      );
  }

  fetchByProjectId = (id: string) : Observable<ResponsePacket<WorkshopSiteVisitModel[]>> => {
    return this.http.get<ResponsePacket<WorkshopSiteVisitModel[]>>(this.FETCH_SITE_VISIT_BY_PROJECT_ID.replace('#', id))
      .pipe(
        catchError(this.handleError)
      );
  }


  scheduleSitVisit = (scheduleReq: SiteVisitRequestModel) : Observable<ResponsePacket<WorkshopSiteVisitModel>> => {
    return this.http.post<ResponsePacket<WorkshopSiteVisitModel>>(this.SCHEDULE_SITE_VISIT_URL, scheduleReq)
      .pipe(
        catchError(this.handleError)
      );
  }

  reScheduleSitVisit = (siteVisitReq: SiteVisitRequestModel, id: string) : Observable<ResponsePacket<WorkshopSiteVisitModel>> => {
    return this.http.patch<ResponsePacket<WorkshopSiteVisitModel>>(this.RE_SCHEDULE_SITE_VISIT_URL.replace('#', id), siteVisitReq)
      .pipe(
        catchError(this.handleError)
      );
  }

  updateSiteVisit = (scheduleReq: SiteVisitRequestModel, id: string) : Observable<ResponsePacket<WorkshopSiteVisitModel>> => {
    return this.http.patch<ResponsePacket<WorkshopSiteVisitModel>>(this.UPDATE_SITE_VISIT_REQUEST.replace('#', id), scheduleReq)
      .pipe(
        catchError(this.handleError)
      );
  }

  updateStatus = (id: string, workshopStatus: string,isAccepted:boolean) : Observable<ResponsePacket<WorkshopSiteVisitModel>> => {
    return this.http.patch<ResponsePacket<WorkshopSiteVisitModel>>(this.REJECT_SITE_VISIT_REQUEST.replace('#', id), {status: workshopStatus,is_accepted:isAccepted})
      .pipe(
        catchError(this.handleError)
      );
  }

  confirmStatus = (id: string, date: string, coord1Id: string, coord2Id: string) : Observable<ResponsePacket<WorkshopSiteVisitModel>> => {
    return this.http.patch<ResponsePacket<WorkshopSiteVisitModel>>(this.CONFIRM_SITE_VISIT_REQUEST.replace('#', id), {
      status: 'CONFIRMED',
      confirm_date : date,
      coordinator :{"_id": coord1Id},
      coordinator2 :{"_id": coord2Id}
    })
      .pipe(
        catchError(this.handleError)
      );
  }

  proposeStatus = (id: string, date: string, coord1Id: string, coord2Id: string) : Observable<ResponsePacket<WorkshopSiteVisitModel>> => {
    return this.http.patch<ResponsePacket<WorkshopSiteVisitModel>>(this.PROPOSE_SITE_VISIT_REQUEST.replace('#', id), {
      proposed_date : date,
      coordinator :{"_id": coord1Id},
      coordinator2 :{"_id": coord2Id}
    })
      .pipe(
        catchError(this.handleError)
      );
  }

  private handleError = (error: any) => {
    let errorMessage = '';
    if (error.error instanceof ErrorEvent) {
      // client-side error
      errorMessage = error.error.error;
    } else {
      // server-side error
      errorMessage = error.error.error;
    }
    return throwError(errorMessage);
  }
}
